/**
 * 控件表面：实色字（hover 不显灰、不用 opacity）、与主按钮统一的胶囊圆角（非像素主题）。
 */

/** 与原生 disabled 配合：禁止误触、淡化、抵消 hover/active 反馈 */
export const chromeDisabledControlClasses =
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:hover:border-line disabled:hover:bg-[var(--menu-solid)] disabled:hover:text-ink-soft'

export const chromeDisabledPrimaryClasses =
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none disabled:hover:opacity-50 disabled:bg-accent disabled:hover:bg-accent disabled:text-white disabled:hover:text-white'

const chromeDisabledDangerToolbarClasses =
  'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none disabled:hover:border-line disabled:hover:bg-[var(--menu-solid)] disabled:hover:text-[var(--color-error)]'

const chromeSurfaceShell =
  'border border-line bg-[var(--menu-solid)] text-ink antialiased outline-none transition hover:border-accent hover:bg-surface-muted hover:text-ink focus-visible:ring-2 focus-visible:ring-accent'

/** 表单控件（输入/多行）：圆角略小，避免 textarea 变成细长胶囊 */
function chromeShapeField(angular: boolean) {
  return angular ? 'rounded-none border-2 shadow-none' : 'rounded-[var(--radius-sm)] shadow-sm'
}

/** 顶栏按钮、芯片、图标按钮：与主按钮一致的胶囊 */
function chromeShapePill(angular: boolean) {
  return angular ? 'rounded-none border-2 shadow-none' : 'rounded-full shadow-sm'
}

export type ChromePrimaryShape = 'pill' | 'rounded'

/** 主按钮圆角：pill 胶囊（默认）；rounded 较小圆角（如错误页重试） */
export function chromeShapePrimaryButton(angular: boolean, shape: ChromePrimaryShape = 'pill') {
  if (angular) return 'rounded-none border-2 border-line'
  return shape === 'rounded' ? 'rounded-[var(--radius-sm)]' : 'rounded-full'
}

export function chromeFieldBaseClass(angular: boolean) {
  return [chromeSurfaceShell, chromeShapeField(angular)].join(' ')
}

/**
 * 生图/智能体等 Composer 多行输入外框：聚焦用 accent 外环 + 偏移，避免 hover 改描边后在移动端与 ring 叠成实心蓝框。
 */
export function chromeComposerTextareaShellClass(angular: boolean) {
  return [
    'border border-line bg-[var(--menu-solid)] text-ink antialiased outline-none transition hover:bg-surface-muted',
    chromeShapeField(angular),
    'flex min-w-0 flex-col overflow-visible px-3 py-2',
    'focus-within:border-line focus-within:ring-2 focus-within:ring-accent',
  ].join(' ')
}

/** 顶栏次要操作（如清空记录） */
export function chromeToolbarButtonClass(angular: boolean) {
  return [
    chromeSurfaceShell,
    chromeShapePill(angular),
    'inline-flex shrink-0 items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold',
    chromeDisabledControlClasses,
  ].join(' ')
}

const chromeDangerToolbarShell =
  'border border-[color-mix(in_srgb,var(--color-error)_52%,var(--line)_48%)] bg-[color-mix(in_srgb,var(--menu-solid)_88%,var(--color-error)_12%)] text-[var(--color-error)] antialiased outline-none transition hover:border-[color-mix(in_srgb,var(--color-error)_68%,var(--line)_32%)] hover:bg-[color-mix(in_srgb,var(--surface-muted)_84%,var(--color-error)_16%)] hover:text-[color-mix(in_srgb,var(--color-error)_82%,var(--ink)_18%)] focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-error)_48%,transparent)]'

/** 列表「删除」等与默认工具栏同级，避免用 Tailwind `!` 覆盖字色 */
export function chromeToolbarDangerClass(angular: boolean) {
  return [
    chromeDangerToolbarShell,
    chromeShapePill(angular),
    'inline-flex shrink-0 items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold',
    chromeDisabledDangerToolbarClasses,
  ].join(' ')
}

/** 仅图标的方形命中区（视觉仍为胶囊圆角裁切） */
export function chromeIconButtonClass(angular: boolean) {
  return [
    chromeSurfaceShell,
    chromeShapePill(angular),
    'inline-flex size-9 shrink-0 items-center justify-center',
    chromeDisabledControlClasses,
  ].join(' ')
}

/** 主按钮 / 上传 label */
export function chromePrimaryClass(
  angular: boolean,
  opts?: { block?: boolean; shape?: ChromePrimaryShape },
) {
  const block = opts?.block
  const shape = opts?.shape ?? 'pill'
  return [
    'inline-flex cursor-pointer items-center justify-center bg-accent font-semibold text-white antialiased outline-none transition hover:bg-accent-hover hover:text-white focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--panel)]',
    chromeDisabledPrimaryClasses,
    block
      ? 'w-full py-3 text-base shadow-sm sm:py-3.5'
      : 'px-4 py-2 text-sm shadow-card sm:px-5 sm:py-2.5',
    chromeShapePrimaryButton(angular, shape),
  ].join(' ')
}

/**
 * @deprecated 请使用 {@link ChromeComposerTextarea}（无滚动条、与生图页一致）
 */
export function chromeTextareaClass(angular: boolean) {
  return [
    chromeFieldBaseClass(angular),
    'glyphicai-scrollbar-none mt-2 w-full resize-none px-3 py-2.5 text-base placeholder:text-ink-soft',
  ].join(' ')
}

/** 参数芯片 / 快捷模板芯片（同一套 hover，避免模板条单独缺样式） */
export function chromeToggleChipClass(angular: boolean, active: boolean) {
  return [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap border px-3 py-2 text-sm font-semibold antialiased transition outline-none focus-visible:ring-2 focus-visible:ring-accent shadow-none hover:shadow-none',
    chromeDisabledControlClasses,
    angular ? 'rounded-none border-2' : 'rounded-full',
    active
      ? 'border-accent bg-accent text-white hover:border-accent hover:bg-accent-hover hover:text-white'
      : 'border-line bg-[var(--menu-solid)] text-ink hover:border-accent hover:bg-surface-muted hover:text-ink',
  ].join(' ')
}

/** 模板条：与 chromeToggleChipClass 完全一致，仅保留命名以方便语义 */
export function chromeTemplateChipClass(angular: boolean, active: boolean) {
  return chromeToggleChipClass(angular, active)
}

/** 分段数字（一次生成张数） */
export function chromeSegmentItemClass(angular: boolean, active: boolean) {
  return [
    'min-w-[2.25rem] px-2 py-1.5 text-sm font-semibold antialiased transition outline-none focus-visible:ring-2 focus-visible:ring-accent',
    angular ? 'rounded-none' : 'rounded-full',
    active
      ? 'bg-accent text-white hover:bg-accent-hover hover:text-white'
      : 'text-ink hover:bg-surface-muted hover:text-ink',
  ].join(' ')
}

/** 弹窗内单行输入 */
export function chromeInputClass(angular: boolean) {
  return [
    chromeFieldBaseClass(angular),
    'mt-1 w-full px-2 py-2 text-sm placeholder:text-ink-soft',
  ].join(' ')
}

/**
 * @deprecated 请使用 {@link ChromeComposerTextarea} variant="form"
 */
export function chromeTextareaSmallClass(angular: boolean) {
  return [
    chromeFieldBaseClass(angular),
    'glyphicai-scrollbar-none mt-1 w-full resize-none px-2 py-2 text-sm placeholder:text-ink-soft',
  ].join(' ')
}

/** 弹窗内主操作小按钮 */
export function chromePrimaryCompactClass(angular: boolean, shape: ChromePrimaryShape = 'pill') {
  return [
    'inline-flex items-center justify-center bg-accent px-3 py-1.5 text-xs font-semibold text-white antialiased outline-none transition hover:bg-accent-hover hover:text-white focus-visible:ring-2 focus-visible:ring-accent',
    chromeDisabledPrimaryClasses,
    chromeShapePrimaryButton(angular, shape),
  ].join(' ')
}

/** 文中次要链接（与 accent 同色、可读下划线） */
export function chromeSecondaryLinkClass(angular: boolean) {
  return [
    'inline text-accent underline-offset-[3px] transition hover:text-accent-hover hover:underline',
    angular ? 'font-mono text-xs font-semibold' : 'text-sm font-semibold',
  ].join(' ')
}

/**
 * 卡片 / 浮层面板外壳：使用主题 `shadow-card`（卡通硬边、像素块影等），避免误用 tailwind `shadow-sm`。
 */
export function chromeCardShellClass(angular: boolean, opts?: { accent?: boolean }): string {
  return [
    'border bg-panel text-ink shadow-card transition-[border-color,box-shadow]',
    opts?.accent
      ? 'border-accent ring-2 ring-inset ring-accent/35'
      : 'border-line',
    angular ? 'rounded-none border-2' : 'rounded-[var(--radius)]',
  ].join(' ')
}

/** 侧栏 / 表单分区卡片：menu-solid 底 + 主题投影 */
export function chromeMenuCardShellClass(angular: boolean, opts?: { interactive?: boolean }): string {
  const hover =
    opts?.interactive === false ? '' : 'transition hover:border-accent hover:bg-surface-muted'
  return [
    'border border-line bg-[var(--menu-solid)] shadow-card',
    hover,
    angular ? 'rounded-none border-2' : 'rounded-[var(--radius-sm)]',
  ].join(' ')
}

/**
 * 元信息标签共用骨架：`.chrome-meta-tag` + flex 布局 + 圆角/描边（不含字号、背景）。
 * {@link chromeMetaTagClass} 与 {@link chromeTimelineKpiChipShellClass} 仅此一处维护几何与主题盒影挂钩类名。
 */
function chromeMetaTagFrameClass(angular: boolean): string {
  const border = angular ? 'border-2 border-line' : 'border border-line'
  const radius = angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]'
  return ['chrome-meta-tag inline-flex shrink-0 items-center px-2 py-0.5 antialiased', radius, border].join(' ')
}

/**
 * 时间线卡片 / 表单旁元信息标签（模型、尺寸、GPT 参数、模板风格等）。
 * 使用主题圆角与直角主题加粗描边；`.chrome-meta-tag` 在 index.css 中按主题追加气质（像素块影等）。
 */
export function chromeMetaTagClass(
  angular: boolean,
  variant: 'solid' | 'outline' | 'mono',
  opts?: { hint?: boolean },
) {
  const bg = variant === 'solid' ? 'bg-surface-muted' : 'bg-transparent'
  const typo =
    variant === 'mono'
      ? 'font-mono text-[10px] font-medium text-ink sm:text-xs'
      : opts?.hint
        ? 'typo-lib-hint font-medium'
        : 'text-[11px] font-medium text-ink sm:text-xs'
  return [chromeMetaTagFrameClass(angular), bg, typo].join(' ')
}

/**
 * 时间线卡片轨上的小号 KPI 胶囊（超清、入库计数等）：与 {@link chromeMetaTagClass} 同圆角、边框与内边距，
 * 使用 semibold + tabular-nums；背景与语义色由调用方 `className` 叠加。
 */
export function chromeTimelineKpiChipShellClass(angular: boolean): string {
  return [
    chromeMetaTagFrameClass(angular),
    'text-[11px] font-semibold leading-none tabular-nums sm:text-xs',
  ].join(' ')
}

/** 可选图卡外壳：内描边选中态，避免 ring 溢出父级圆角 */
export function chromeSelectableMediaCardClass(angular: boolean, selected: boolean): string {
  return [
    'relative isolate overflow-hidden border bg-surface-muted transition-[border-color,box-shadow]',
    angular ? 'rounded-none border-2' : 'rounded-xl border',
    selected
      ? 'border-accent shadow-[inset_0_0_0_2px_var(--accent)]'
      : 'border-line hover:border-accent',
  ].join(' ')
}

/** 角标：序号 / 已选 / 已在图库 */
export function chromeSelectableMediaCornerBadgeClass(
  angular: boolean,
  tone: 'index' | 'selected' | 'saved' | 'idle',
): string {
  const base = [
    'pointer-events-none absolute z-[2] max-w-[calc(100%-0.75rem)] truncate border px-1.5 py-0.5 text-[10px] font-semibold leading-none antialiased',
    angular ? 'rounded-none border-2' : 'rounded-md border',
  ]
  if (tone === 'index') {
    return [...base, 'left-1.5 top-1.5 border-line bg-[color-mix(in_srgb,var(--panel)_92%,transparent)] text-ink'].join(' ')
  }
  if (tone === 'saved') {
    return [
      ...base,
      'right-1.5 top-1.5 border-accent/35 bg-[color-mix(in_srgb,var(--accent)_16%,var(--panel))] text-ink',
    ].join(' ')
  }
  if (tone === 'selected') {
    return [...base, 'right-1.5 top-1.5 border-accent bg-accent text-white'].join(' ')
  }
  return [
    ...base,
    'right-1.5 top-1.5 border-line bg-[color-mix(in_srgb,var(--panel)_90%,transparent)] text-ink-soft',
  ].join(' ')
}
